/**
 * INCSUBPoolInfoCard.jsx
 *
 * This component displays a card for a pool's summary, including:
 * - Pool name, type, state, validity
 * - Host details
 * - Pool metrics (created date, size, earnings, plan price)
 * - Start and end dates
 * - Suspend button (if applicable)
 * @author vishal Lohbande
 */

import React from 'react';
import './poolInfo.css';

/**
 * INCSUBPoolInfoCard Component
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.pool - Pool object containing all necessary details
 * @param {Function} props.fun - Callback function to suspend the pool (invoked with poolId, hostId)
 *
 * @returns {JSX.Element} Rendered UI card for a pool
 */
function INCSUBPoolInfoCard({ pool, fun }) {

  /**
   * Converts an ISO date string to a more readable format: 'DD Month YYYY'
   *
   * @param {string} dateStr - Date string (ISO or similar)
   * @returns {string} Formatted date string
   */
  const convertDateFormat = (dateStr) => {
    const dateObj = new Date(dateStr);
    const options = { day: '2-digit', month: 'long', year: 'numeric' };
    return dateObj.toLocaleDateString('en-GB', options);
  };

  return (
    <div className='INCSUBPoolInfo_card'>

      {/* Pool name, type, status, validity */}
      <div className='INCSUBPoolInfo_card_poolname_status_validity'>
        <div className='INCSUBPoolInfo_card_poolname_type'>
          <div className='INCSUBPoolInfo_card_poolname'>{pool.poolName}</div>
          <div className={`INCSUBPoolInfo_card_pooltype ${pool.poolType}`}>{pool.poolType}</div>
        </div>

        <div className='INCSUBPoolInfo_card_status_validity'>
          <div className='INCSUBPoolInfo_card_status_validity_btns'>
            <div className='INCSUBPoolInfo_validity' style={{cursor:"pointer"}}>{`${pool.poolValidDaysCount} days`}</div>
            <p>{pool.poolType==='Standard' && pool.poolState==='Active'?"Validity":"Validity left"}</p>
          </div>
          <div className={`INCSUBPoolInfo_active_status ${pool.poolState}`}>
            <div className='INCSUBPoolInfo_active_status_disc'></div>
            <div className='INCSUBPoolInfo_active_status_text'>{pool.poolState}</div>
          </div>
        </div>
      </div>

      {/* Pool detail sections */}
      <div className='INCSUBPoolInfo_card_pool_details'>

        {/* Hosted By */}
        <div className='INCSUBPoolInfo_card_pool_details_divs'>
          <div className='INCSUBPoolInfo_card_pool_details_name_img'>
            <div className='INCSUBPoolInfo_card_pool_details_hostedby_img'>
              <img className='INCSUB_UserImgTag' src={pool.hostImage?pool.hostImage:'/assets/icons/common/defaultImage.png'} alt='default img' />
            </div>
            <div className='INCSUBPoolInfo_card_pool_details_hostedby_name'>
              <p className='INCSUBPoolInfo_card_pool_details_hostedby_p'>Hosted By</p>
              <p className='INCSUBPoolInfo_card_pool_details_hostedbyname_p'>{pool.hostName}</p>
            </div>
          </div>
        </div>

        {/* Created Date */}
        <div className='INCSUBPoolInfo_card_pool_details_divs'>
          <div className='INCSUBPoolInfo_card_pool_details_name_img'>
            <div className='INCSUBPoolInfo_card_pool_details_hostedby_name'>
              <p className='INCSUBPoolInfo_card_pool_details_hostedby_p'>Created Date</p>
              <p className='INCSUBPoolInfo_card_pool_details_hostedbyname_p'>{convertDateFormat(pool.poolCreatedAt)}</p>
            </div>
          </div>
        </div>

        {/* Pool Size */}
        <div className='INCSUBPoolInfo_card_pool_details_divs'>
          <div className='INCSUBPoolInfo_card_pool_details_name_img'>
            <div className='INCSUBPoolInfo_card_pool_details_hostedby_name'>
              <p className='INCSUBPoolInfo_card_pool_details_hostedby_p'>Size</p>
              <p className='INCSUBPoolInfo_card_pool_details_hostedbyname_p'>{pool.poolMemberSize} {pool?.poolMemberSize>1?"Members":"Member"}</p>
            </div>
          </div>
        </div>

        {/* Plan Price */}
        <div className='INCSUBPoolInfo_card_pool_details_divs'>
          <div className='INCSUBPoolInfo_card_pool_details_name_img'>
            <div className='INCSUBPoolInfo_card_pool_details_hostedby_name'>
              <p className='INCSUBPoolInfo_card_pool_details_hostedby_p'>Plan Price</p>
              <p className='INCSUBPoolInfo_card_pool_details_hostedbyname_p'>₹ {pool.poolPlanPrice}</p>
            </div>
          </div>
        </div>

        {/* Earning */}
        <div className='INCSUBPoolInfo_card_pool_details_divs'>
          <div className='INCSUBPoolInfo_card_pool_details_name_img'>
            <div className='INCSUBPoolInfo_card_pool_details_hostedby_name'>
              <p className='INCSUBPoolInfo_card_pool_details_hostedby_p'>Earning</p>
              <p className='INCSUBPoolInfo_card_pool_details_hostedbyname_p'>₹ {pool.hostEarning}</p>
            </div>
          </div>
        </div>

        {/* Pool Start Date */}
        <div className='INCSUBPoolInfo_card_pool_details_divs'>
          <div className='INCSUBPoolInfo_card_pool_details_name_img'>
            <div className='INCSUBPoolInfo_card_pool_details_hostedby_name'>
              <p className='INCSUBPoolInfo_card_pool_details_hostedby_p'>Pool Start Date</p>
              <p className='INCSUBPoolInfo_card_pool_details_hostedbyname_p'>{pool.poolStartDate}</p>
            </div>
          </div>
        </div>

        {/* Pool End Date */}
        <div className='INCSUBPoolInfo_card_pool_details_divs_last'>
          <div className='INCSUBPoolInfo_card_pool_details_name_img'>
            <div className='INCSUBPoolInfo_card_pool_details_hostedby_name'>
              <p className='INCSUBPoolInfo_card_pool_details_hostedby_p'>Pool End Date</p>
              <p className='INCSUBPoolInfo_card_pool_details_hostedbyname_p'>{pool.poolEndDate}</p>
            </div>
          </div>
        </div>

        {/* Suspend Button (only if not already suspended) */}
        <div className='INCSUBPoolInfo_card_pool_details_suspendBtn'>
          {pool.poolState !== 'Suspended' && (
            <button
              className='suspend'
              onClick={() => fun(pool.poolId, pool.hostId, "Host")}
            >
              Suspend Host
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default INCSUBPoolInfoCard;
